import clsx from 'clsx'
import { UserPlus, X } from 'lucide-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { ROLE, isOperationalRole, isPrivilegedRole } from '@ankor-io/common/domain/iam/account'
import { InviteStatus, Role } from '@ankor-io/common/types/account'
import { AwxConnectedAccountType } from '@ankor-io/common/types/awx'
import { getUserRoleText } from '@ankor-io/common/util/enum-to-string/user.role'
import { Button } from '@ankor-io/ui-components/src/Button'
import { Dialog, DialogActions, DialogBody, DialogTitle } from '@ankor-io/ui-components/src/Dialog'
import { ErrorMessage, Field, Label } from '@ankor-io/ui-components/src/Fieldset'
import { Input } from '@ankor-io/ui-components/src/Input'
import { Select } from '@ankor-io/ui-components/src/Select'
import { Switch, SwitchField, SwitchGroup } from '@ankor-io/ui-components/src/Switch'

import { useListOrganizationVesselsQuery } from '@/api/queries/useVesselQuery'
import { useConnectedAccount } from '@/providers/connectedAccount'
import { useUserContext } from '@/providers/user/useUserContext'
import { zodResolver } from '@/utils/zodResolver'

type UserInfo = { id: string; email: string; name: string }
type InviteInfo = { id: string; email: string; status: InviteStatus }

const createFormSchema = (
  existingUsers: UserInfo[] = [],
  existingInvites: InviteInfo[] = [],
  enforceInviteToConnectedAccountId?: string,
) => {
  return z
    .object({
      email: z
        .string()
        .email({
          message: 'Invalid email address.',
        })
        .superRefine((email, ctx) => {
          const normalizedEmail = email.trim().toLowerCase()
          const isAlreadyMember = existingUsers.some((user) => user.email.toLowerCase() === normalizedEmail)
          if (isAlreadyMember) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'User is already a member of this organization',
            })
          }

          const hasPendingInvite = existingInvites.some(
            (invite) =>
              invite.email.toLowerCase() === normalizedEmail &&
              [InviteStatus.SENDING, InviteStatus.SENT, InviteStatus.PENDING, InviteStatus.SIGNING_UP].includes(
                invite.status,
              ),
          )

          if (hasPendingInvite) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'User already has a pending invitation',
            })
          }
        }),
      role: z.string().min(1, {
        message: 'Please select a role for the user.',
      }),
      connectedAccountId: z.string().optional(),
      vesselId: z.string().optional(),
      awxConnectedAccountIds: z.array(z.string()).optional(),
    })
    .superRefine((data, ctx) => {
      if (data.role && isOperationalRole(data.role as Role)) {
        return
      }

      if (enforceInviteToConnectedAccountId) {
        if (!data.awxConnectedAccountIds?.length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Please select at least one module.',
            path: ['awxConnectedAccountIds'],
          })
        }

        return
      }

      if (!data.vesselId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please select a vessel.',
          path: ['vesselId'],
        })
      }

      if (!data.awxConnectedAccountIds?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please select at least one module.',
          path: ['awxConnectedAccountIds'],
        })
      }
    })
}

type InviteUserModalProps = {
  isOpen: boolean
  enforceInviteToConnectedAccountId?: string
  onClose: () => void
  onInvite: (userData: {
    email: string
    role: Role
    connectedAccountId?: string
    vesselId?: string
    awxConnectedAccountIds?: string[]
  }) => void
  existingUsers: UserInfo[]
  existingInvites: InviteInfo[]
  isSubmitting?: boolean
}

const InviteUserModal = ({
  isOpen,
  enforceInviteToConnectedAccountId,
  onClose,
  onInvite,
  existingUsers = [],
  existingInvites = [],
  isSubmitting = false,
}: InviteUserModalProps) => {
  const { activeOrganizationRole } = useUserContext()
  const { connectedAccounts } = useConnectedAccount()
  const { data: vesselsData } = useListOrganizationVesselsQuery({
    enabled: isOpen && !enforceInviteToConnectedAccountId,
  })

  const vessels = vesselsData?.vessels ?? []
  const formSchema = createFormSchema(existingUsers, existingInvites, enforceInviteToConnectedAccountId)
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      role: '',
      connectedAccountId: enforceInviteToConnectedAccountId || '',
      vesselId: '',
      awxConnectedAccountIds: [] as string[],
    },
  })

  const watchedVesselId = form.watch('vesselId')
  const selectedVesselId = enforceInviteToConnectedAccountId ? undefined : (watchedVesselId as string | undefined)

  // Vessel modules: when enforcing (vessel page) use context; otherwise use selected vessel from list
  const selectedVesselConnectedAccounts = vessels.find((v) => v.vessel.id === watchedVesselId)?.connectedAccounts ?? []
  const vesselConnectedAccounts = enforceInviteToConnectedAccountId
    ? connectedAccounts
    : selectedVesselConnectedAccounts
  const apaAccount = vesselConnectedAccounts.find((a) => a.type === AwxConnectedAccountType.APA)
  const opsAccount = vesselConnectedAccounts.find((a) => a.type === AwxConnectedAccountType.OPERATIONS)
  const showModuleToggles = (enforceInviteToConnectedAccountId && (apaAccount || opsAccount)) || !!selectedVesselId

  // Keep track of the selected modules in the form
  const watchedAwxConnectedAccountIds = form.watch('awxConnectedAccountIds') ?? []
  const isApaChecked = apaAccount && watchedAwxConnectedAccountIds.includes(apaAccount.id)
  const isOpsChecked = opsAccount && watchedAwxConnectedAccountIds.includes(opsAccount.id)

  // Enable toggles for modules the vessel actually has; if vessel has only one module, auto-select and disable that toggle.
  // When both modules exist: disable a toggle only when it is the sole selected one (so it looks disabled and can't be unchecked).
  const hasSingleModule = vesselConnectedAccounts.length === 1
  const singleModuleAccount = hasSingleModule ? vesselConnectedAccounts[0] : null
  const isApaDisabled =
    !apaAccount ||
    (hasSingleModule && singleModuleAccount?.type === AwxConnectedAccountType.APA) ||
    (isApaChecked && !isOpsChecked)
  const isOpsDisabled =
    !opsAccount ||
    (hasSingleModule && singleModuleAccount?.type === AwxConnectedAccountType.OPERATIONS) ||
    (isOpsChecked && !isApaChecked)

  useEffect(() => {
    if (!selectedVesselId || enforceInviteToConnectedAccountId) {
      return
    }

    form.setValue('awxConnectedAccountIds', [])
  }, [selectedVesselId, enforceInviteToConnectedAccountId, form])

  useEffect(() => {
    if (!selectedVesselId || !singleModuleAccount?.id) {
      return
    }

    form.setValue('awxConnectedAccountIds', [singleModuleAccount.id])
  }, [selectedVesselId, singleModuleAccount?.id, form])

  useEffect(() => {
    if (!isOpen) {
      form.reset()
      return
    }

    if (enforceInviteToConnectedAccountId) {
      form.setValue('connectedAccountId', enforceInviteToConnectedAccountId)
      // Single module: pre-select and disable; both modules: start with none selected (user must pick at least one)
      const initialModules = connectedAccounts.length === 1 ? [connectedAccounts[0].id] : []
      form.setValue('awxConnectedAccountIds', initialModules)
      return
    }

    form.setValue('vesselId', '')
    form.setValue('awxConnectedAccountIds', [])
  }, [isOpen, enforceInviteToConnectedAccountId, connectedAccounts, form])

  const handleInvite = (values: z.infer<typeof formSchema>) => {
    onInvite({
      email: values.email,
      role: values.role as Role,
      connectedAccountId: values.connectedAccountId,
      vesselId: values.vesselId,
      awxConnectedAccountIds: values.awxConnectedAccountIds,
    })
  }

  const toggleModule = (connectedAccountId: string, checked: boolean) => {
    const current = form.getValues('awxConnectedAccountIds') ?? []
    const next = checked ? [...current, connectedAccountId] : current.filter((id) => id !== connectedAccountId)
    if (!checked && next.length === 0) {
      return
    }

    form.setValue('awxConnectedAccountIds', next)
    if (next.length > 0) {
      form.clearErrors('awxConnectedAccountIds')
    }
  }

  const handleCancel = () => {
    onClose()
  }

  const isRoleInvitableByUser = (invitingRole: Role) => {
    const currentRole = ROLE(activeOrganizationRole)
    return currentRole.isEqualOrHigherThan(invitingRole)
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleCancel}
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <UserPlus className="size-5 text-gray-900 dark:text-white" />
          <DialogTitle>Invite User</DialogTitle>
        </div>
        <Button
          plain
          onClick={handleCancel}
        >
          <X data-slot="icon" />
        </Button>
      </div>

      <DialogBody>
        <form
          onSubmit={form.handleSubmit(handleInvite)}
          className="space-y-4"
        >
          <Field>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter user email"
              autoComplete="off"
              {...form.register('email')}
            />
            {form.formState.errors.email && (
              <ErrorMessage>{form.formState.errors.email.message || 'Invalid email'}</ErrorMessage>
            )}
          </Field>

          <Field>
            <Label htmlFor="role">Role</Label>
            <Select
              id="role"
              {...form.register('role')}
            >
              <option value="">Select a role</option>
              {Object.values(Role).map((role) => (
                <option
                  key={role}
                  value={role}
                  disabled={!isRoleInvitableByUser(role)}
                >
                  {getUserRoleText(role)}
                </option>
              ))}
            </Select>
            {form.formState.errors.role && <ErrorMessage>{form.formState.errors.role.message}</ErrorMessage>}
          </Field>

          {!isPrivilegedRole(form.watch('role') as Role) && (
            <Field>
              <Label htmlFor={enforceInviteToConnectedAccountId ? 'connectedAccount' : 'vessel'}>Vessel</Label>
              {enforceInviteToConnectedAccountId ? (
                <Select
                  id="connectedAccount"
                  disabled
                  {...form.register('connectedAccountId')}
                >
                  <option value="">Select a vessel</option>
                  {connectedAccounts.map((connectedAccount) => (
                    <option
                      key={connectedAccount.id}
                      value={connectedAccount.id}
                    >
                      {connectedAccount.name}
                    </option>
                  ))}
                </Select>
              ) : (
                <Select
                  id="vessel"
                  {...form.register('vesselId')}
                >
                  <option value="">Select a vessel</option>
                  {vessels.map((v) => (
                    <option
                      key={v.vessel.id}
                      value={v.vessel.id}
                    >
                      {v.vessel.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}

          {!isOperationalRole(form.watch('role') as Role) && showModuleToggles && (
            <Field className="mb-0!">
              <Label htmlFor="modules">Modules</Label>
              <SwitchGroup id="modules">
                {apaAccount && (
                  <SwitchField>
                    <Switch
                      data-testid="module-apa-toggle"
                      checked={isApaChecked}
                      disabled={isApaDisabled}
                      className={clsx(
                        isApaDisabled && 'cursor-not-allowed!',
                        isApaChecked && 'bg-primary-600! dark:bg-primary-500!',
                      )}
                      onChange={(checked) => toggleModule(apaAccount.id, checked)}
                    />
                    <span
                      className="text-sm font-medium text-gray-900 dark:text-white"
                      data-slot="label"
                    >
                      APA Management
                    </span>
                  </SwitchField>
                )}
                {opsAccount && (
                  <SwitchField>
                    <Switch
                      data-testid="module-ops-toggle"
                      checked={isOpsChecked}
                      disabled={isOpsDisabled}
                      className={clsx(
                        isOpsDisabled && 'cursor-not-allowed!',
                        isOpsChecked && 'bg-primary-600! dark:bg-primary-500!',
                      )}
                      onChange={(checked) => toggleModule(opsAccount.id, checked)}
                    />
                    <span
                      className="text-sm font-medium text-gray-900 dark:text-white"
                      data-slot="label"
                    >
                      Operations
                    </span>
                  </SwitchField>
                )}
              </SwitchGroup>
              {form.formState.errors.awxConnectedAccountIds && (
                <ErrorMessage>{form.formState.errors.awxConnectedAccountIds.message}</ErrorMessage>
              )}
            </Field>
          )}

          <DialogActions>
            <Button
              outline
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              className="data-disabled:opacity-50 data-disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Invite'}
            </Button>
          </DialogActions>
        </form>
      </DialogBody>
    </Dialog>
  )
}

export default InviteUserModal
